import React, { useContext, useState, useRef } from "react";
import { executeActions, getWidgetSpecificIsVisible } from "@utils/index";
import { connect } from "react-redux";
import { useOnload } from "@utils/useOnLoad";
import { Context } from "@components/useContext/context";
import ImageContainer from "@components/imageContainer";
import { resolvePath } from "@utils/resolvePath";
import { CommonProps } from "@utils/interface";
import CloneChildren from "@components/children";
import { getInrFormat, convertToLakh } from "@utils/inrFormatter";
import {
  SuperContainer,
  Container,
  Header,
  Logo,
  HeaderText,
  CarouselContainer,
  CarouselWrapper,
  CarCard,
  CarImageContainer,
  CarDetails,
  CarName,
  LocationText,
  PriceContainer,
  Price,
  SavingsText,
  NavigationDots,
  Dot,
  ExploreButton
} from "./styles";

interface ICarDetail {
  brand_id: number;
  model_id: number;
  brand_name: string;
  model_name: string;
  model_slug: string;
  min_price: number;
  max_price: number;
  min_acko_drive_savings: number;
  heroImageUrl: string;
}

interface IAckoDriveCarousel extends CommonProps {
  title?: string;
  logoSrc?: string;
  buttonText?: string;
  buttonIcon?: string;
  brandColor?: string;
  carDetails?: Array<ICarDetail>;
  backgroundImageUrl?: string;
  city?: string;
}

const mapStateToProps = function (state: any, ownProps: any) {
  return {
    is_visible: getWidgetSpecificIsVisible(
      state.configuration?.normalisedConfig,
      ownProps.id
    )
  };
};

const AckoDriveCarousel = ({ children, ...props }: IAckoDriveCarousel) => {
  const {
    is_visible,
    actions,
    title,
    carDetails = [],
    buttonText,
    buttonIcon,
    city,
    styles,
    mStyles
  } = props;

  const { on_load, on_click, on_card_click } = actions || {};
  const { isDesktop } = useContext(Context);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useOnload({ on_load });

  const handleExploreClick = () => {
    executeActions(on_click);
  };
  const handleCarClick = (car: ICarDetail) => {
    const redirectUrl = `https://ackodrive.com/cars/${car.model_slug}`;
    executeActions(on_card_click, {
      customData: {
        redirect_url: redirectUrl
      }
    });
    window.location.href = redirectUrl;
  };

  const handleDotClick = (index: number) => {
    setCurrentSlide(index);
    if (carouselRef.current) {
      if (isDesktop) {
        // Desktop: Fixed card width 320px + gap
        const cardWidth = 320;
        const gap = 20;
        carouselRef.current.scrollTo({
          left: index * (cardWidth + gap),
          behavior: "smooth"
        });
      } else {
        // Mobile: Scroll by container width
        const containerWidth = carouselRef.current.clientWidth;
        carouselRef.current.scrollTo({
          left: index * containerWidth,
          behavior: "smooth"
        });
      }
    }
  };

  const handleScroll = () => {
    if (carouselRef.current) {
      if (isDesktop) {
        // Desktop: Fixed card width 320px + gap
        const cardWidth = 240;
        const gap = 20;
        const scrollLeft = carouselRef.current.scrollLeft;
        const newIndex = Math.round(scrollLeft / (cardWidth + gap));
        setCurrentSlide(Math.min(newIndex, carDetails.length - 1));
      } else {
        // Mobile: Calculate based on container width
        const containerWidth = carouselRef.current.clientWidth;
        const scrollLeft = carouselRef.current.scrollLeft;
        const newIndex = Math.round(scrollLeft / containerWidth);
        setCurrentSlide(newIndex);
      }
    }
  };

  if (!is_visible) {
    return null;
  }
  if (!(carDetails?.length > 1)) {
    return null;
  }

  const formatSavings = (savings: number) => {
    return `₹${Math.round(savings).toLocaleString("en-US")} less than dealer price`;
  };

  return (
    <SuperContainer>
      <Container styles={styles} mStyles={mStyles}>
        <Header>
          <Logo>
            <ImageContainer
              imgSrc={resolvePath("/images/AckodriveLogo.svg")}
              alt="ACKO Drive"
              width={isDesktop ? 160 : 80}
              height={isDesktop ? 80 : 40}
            />
          </Logo>
          <HeaderText>{title}</HeaderText>
        </Header>

        <CarouselContainer>
          <CarouselWrapper ref={carouselRef} onScroll={handleScroll}>
            {carDetails?.map((car, index) => (
              <CarCard
                key={`${car.brand_id}-${car.model_id}`}
                onClick={() => handleCarClick(car)}
              >
                <CarImageContainer
                  backgroundImage={resolvePath(
                    `/images/acko_drive-bg-${(index % 5) + 1}.svg`
                  )}
                >
                  <img
                    src={
                      car.heroImageUrl ||
                      resolvePath("/images/sampleCarImage.svg")
                    }
                    alt={car.model_name}
                    style={{
                      height: "200px",
                      width: "234px",
                      objectFit: "contain",
                      objectPosition: "center",
                      position: "relative",
                      zIndex: 1
                    }}
                  />
                </CarImageContainer>

                <CarDetails>
                  <CarName>{car.model_name}</CarName>
                  {city && (
                    <LocationText>
                      On-road price in {city} starts from
                    </LocationText>
                  )}

                  <PriceContainer>
                    <Price>₹{convertToLakh(car.min_price)} lakh</Price>
                    {"  "}
                    <SavingsText>
                      {formatSavings(car.min_acko_drive_savings)}
                    </SavingsText>
                  </PriceContainer>
                </CarDetails>
              </CarCard>
            ))}
          </CarouselWrapper>
        </CarouselContainer>

        {carDetails?.length > 1 && (
          <NavigationDots>
            {carDetails.map((_, index) => (
              <Dot
                key={index}
                active={index === currentSlide}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </NavigationDots>
        )}

        <ExploreButton onClick={handleExploreClick}>
          {buttonIcon && (
            <ImageContainer
              imgSrc={resolvePath("/images/AckoDriveLogo2.svg")}
              alt="explore icon"
              width={20}
              height={20}
              styles={{
                margin: "0 16px"
              }}
            />
          )}
          {buttonText}
        </ExploreButton>

        <CloneChildren
          children={children}
          props={props}
          styles={styles}
          mStyles={mStyles}
        />
      </Container>
    </SuperContainer>
  );
};

export default connect(mapStateToProps)(AckoDriveCarousel);
